import { Box, Grid } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useState } from 'react';
import type { Control, FieldErrors } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { LuEye, LuEyeOff } from 'react-icons/lu';
import { MdSend } from 'react-icons/md';
import * as yup from 'yup';
import FaceliftButton from '@/components/Atoms/FaceliftButton';
import FaceliftIconButton from '@/components/Atoms/FaceliftIconButton';
import FaceliftInput from '@/components/Atoms/FaceliftInput';
import { useUpdatePassword } from '@/hooks/service-hooks/auth';
import type { ProfileFormData } from './General';

interface InformationProps {
	control: Control<ProfileFormData>;
	errors: FieldErrors<ProfileFormData>;
	email: string;
	onPasswordUpdateSuccess: (message: string) => void;
}

interface PasswordFormData {
	currentPassword: string;
}

const passwordSchema: yup.ObjectSchema<PasswordFormData> = yup.object().shape({
	currentPassword: yup
		.string()
		.required('Current password is required'),
});

const Information = ({
	control,
	errors,
	email,
	onPasswordUpdateSuccess,
}: InformationProps) => {
	const [showPassword, setShowPassword] = useState(false);
	const {
		control: passwordControl,
		handleSubmit: handlePasswordSubmit,
		formState: { isSubmitting: isPasswordSubmitting },
		reset: resetPasswordForm,
		formState: { isDirty },
	} = useForm<PasswordFormData>({
		resolver: yupResolver(passwordSchema),
		defaultValues: {
			currentPassword: '',
		},
		mode: 'onBlur',
	});

	const {
		mutateAsync: updatePassword,
		isLoading: updatePasswordLoading,
	} = useUpdatePassword(
		(data) => {
			onPasswordUpdateSuccess(data.message);
			resetPasswordForm();
		},
		(error) => {
			const apiError = error as { response?: { data?: { error?: { message?: string } } } };
			toast.error(
				apiError?.response?.data?.error?.message ||
				'Failed to update password. Please try again.',
			);
		},
	);


	return (
		<Box>
			<Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
				<Controller
					name="firstName"
					control={control}
					render={({ field }) => (
						<FaceliftInput
							{...field}
							topLabel="First Name"
							placeholder="Enter first name"
							error={errors.firstName?.message}
							required
						/>
					)}
				/>

				<Controller
					name="lastName"
					control={control}
					render={({ field }) => (
						<FaceliftInput
							{...field}
							topLabel="Last Name"
							placeholder="Enter last name"
							error={errors.lastName?.message}
							required
						/>
					)}
				/>

				<Controller
					name="role"
					control={control}
					render={({ field }) => (
						<FaceliftInput
							{...field}
							topLabel="Role"
							placeholder="Enter role"
							error={errors.role?.message}
							disabled
						/>
					)}
				/>

				<Controller
					name="email"
					control={control}
					render={({ field }) => (
						<FaceliftInput
							{...field}
							type="email"
							topLabel="Email"
							placeholder="Enter email"
							error={errors.email?.message}
							disabled
						/>
					)}
				/>

				<Box display="flex" alignItems="flex-end" gap={2}>
					<Box width="100%">
						<Controller
							name="currentPassword"
							control={passwordControl}
							render={({ field }) => (
								<FaceliftInput
									{...field}
									type={showPassword ? 'text' : 'password'}
									topLabel="Password"
									placeholder="Enter current password"
									endIcon={
										<FaceliftIconButton
											bg="transparent"
											variant="ghost"
											aria-label="toggle password visibility"
											size="xs"
											onClick={() => setShowPassword(!showPassword)}
											_hover={{
												bg: { _dark: 'gray.800', _light: 'gray.300' },
											}}
											color={{ _dark: 'brand', _light: 'brand.500' }}
										>
											{showPassword ? (
												<LuEye size={16} />
											) : (
												<LuEyeOff size={16} />
											)}
										</FaceliftIconButton>
									}
								/>
							)}
						/>
					</Box>
					<FaceliftButton
						type="button"
						variant="solid"
						onClick={handlePasswordSubmit((data: PasswordFormData) => {
							updatePassword({
								email,
								currentPassword: data.currentPassword,
							});
						})}
						endIcon={<MdSend />}
						height="25px"
						loading={isPasswordSubmitting || updatePasswordLoading}
						disabled={isPasswordSubmitting || updatePasswordLoading || !isDirty}
					>
						Send Request
					</FaceliftButton>
				</Box>
			</Grid>
		</Box>
	);
};

export default Information;
